import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar as CalendarIcon,
  Plus,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useMemberCalendarAccess } from "@/hooks/useMemberCalendarAccess";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";
import { useGoogleCalendars } from "@/hooks/useGoogleCalendars";
import {
  getCalendarAccentColor,
  getCalendarLabelByGoogleId,
  listEnabledSyncCalendars,
} from "@/lib/gcal-calendar-colors";
import { isAppointmentVisibleToMember } from "@/lib/appointment-filters";
import { useGoogleCalendarStatus } from "@/hooks/useGoogleCalendarStatus";
import { useGoogleCalendarAutoSync } from "@/hooks/useGoogleCalendarAutoSync";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { invokeGoogleCalendar } from "@/lib/google-calendar-invoke";
import { useToast } from "@/hooks/use-toast";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  parseISO,
  isSameDay,
  addWeeks,
  subWeeks,
  startOfMonth,
  endOfMonth,
  getDaysInMonth,
  addDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  startOfYear,
  endOfYear,
  eachMonthOfInterval,
  startOfDay,
  endOfDay,
  isBefore,
  addMinutes,
  getWeek,
  max,
  min,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { AppointmentDetailsSheet } from "@/components/appointments/AppointmentDetailsSheet";
import { useAppointmentDraft } from "@/contexts/AppointmentDraftContext";
import { useAppointmentsCalendarFilter } from "@/contexts/AppointmentsCalendarFilterContext";
import { GoogleCalendarReconnectBadge } from "@/components/layout/GoogleCalendarReconnectBadge";
import { GoogleCalendarAgendaReconnectAlert } from "@/components/appointments/GoogleCalendarAgendaReconnectAlert";
import { CalendarSyncDropdown } from "@/components/appointments/CalendarSyncDropdown";
import {
  appointmentOverlapsDay,
  getAllDayCalendarDayCount,
  getAllDaySpanInWeek,
  getAppointmentEnd,
  getAppointmentStart,
  buildTimedEventRenderPieces,
  getTimedEventPositionStyle,
} from "@/lib/appointment-calendar";

import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";

interface AppointmentRecord {
  id: string;
  lead_id: string | null;
  scheduled_date: string;
  duration_minutes: number;
  google_event_id?: string;
  google_calendar_id?: string | null;
  service_name?: string;
  reason?: string;
  notes?: string;
  status: string;
  summary?: string;
  attendee_name?: string;
  attendee_email?: string;
  origin_source?: string;
  sync_status?: string;
  is_all_day?: boolean;
}

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  current_appointment_id?: string;
  current_appointment_date?: string;
  current_appointment_duration?: number;
  stage: string;
  notes?: string;
  metadata?: any;
  appointmentId?: string;
  assigned_member_id?: string | null;
}

interface CalendarEvent {
  id: string;
  appointmentId: string;
  title: string;
  start: Date;
  end: Date;
  color: string;
  lead: Lead | null;
  google_event_id?: string;
  google_calendar_id?: string | null;
  calendarAccentColor?: string | null;
  duration_minutes?: number;
  service_name?: string;
  reason?: string;
  notes?: string;
  summary?: string;
  attendee_name?: string;
  attendee_email?: string;
  origin_source?: string;
  sync_status?: string;
  isAllDay?: boolean;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0h às 23h (24 horas completas)

type ViewType = "day" | "week" | "month" | "year" | "schedule";

// Componente Draggable para eventos
function DraggableEvent({
  event,
  position,
  onEventClick,
}: {
  event: CalendarEvent;
  position: {
    top: string;
    height: string;
    left: string;
    width: string;
    column: number;
    totalColumns: number;
  };
  onEventClick: (event: CalendarEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: event.id,
    data: { event },
  });

  const durationMinutes = Math.max(0, (event.end.getTime() - event.start.getTime()) / (1000 * 60));
  const isOverlapping = position.totalColumns > 1;
  const isFrontCard = isOverlapping && position.column > 0;
  const isCompact = durationMinutes <= 30 || (isOverlapping && position.column > 0);
  const showTimeRange = isOverlapping && position.column === 0 && durationMinutes > 30;

  const style = {
    top: position.top,
    height: position.height,
    left: position.left,
    width: position.width,
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 10 + position.column,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "absolute overflow-hidden rounded-[4px] px-1.5 text-xs text-white cursor-grab transition-all",
        "shadow-[inset_0_0_0_1px_rgba(0,0,0,0.45)]",
        "hover:brightness-110",
        isOverlapping && "px-1",
        isFrontCard && "font-medium",
        isDragging && "cursor-grabbing z-50 ring-2 ring-primary",
      )}
      style={{
        ...style,
        backgroundColor: event.color,
        ...(event.calendarAccentColor
          ? { borderLeft: `4px solid ${event.calendarAccentColor}` }
          : {}),
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDragging) {
          onEventClick(event);
        }
      }}
    >
      <div
        className={cn(
          "flex h-full min-h-0",
          isCompact ? "items-center gap-1" : "flex-col justify-center gap-0.5 py-0.5",
        )}
      >
        {isCompact ? (
          <p className="min-w-0 truncate leading-tight">
            <span className="font-semibold">{event.title}</span>
            <span className="text-white/90">, {format(event.start, "HH:mm")}</span>
          </p>
        ) : (
          <>
            <div className="min-w-0 truncate font-semibold leading-tight tracking-tight">
              {event.title}
            </div>
            <div className="shrink-0 text-[11px] font-medium leading-tight text-white/90">
              {showTimeRange
                ? `${format(event.start, "H")} - ${format(event.end, "H")}`
                : format(event.start, "HH:mm")}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Componente Droppable para slots de horário
function DroppableSlot({
  id,
  hour,
  onClick,
  isHighlighted,
}: {
  id: string;
  hour: number;
  onClick: () => void;
  isHighlighted?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "h-[60px] border-b border-border cursor-pointer transition-colors",
        isOver && "bg-primary/20",
        isHighlighted && "bg-primary/5",
        !isOver && !isHighlighted && "hover:bg-muted/50"
      )}
      onClick={onClick}
    />
  );
}

export default function Appointments() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { memberId, canSeeAllCalendars, canSeeAllAppointments, appointmentsCalendarVisibility, isAdmin } =
    useMemberPermissions();
  const { allowedGoogleCalendarIds } = useMemberCalendarAccess({
    workspaceId,
    memberId,
    canSeeAllCalendars,
  });
  const { connections: gcalConnections } = useGoogleCalendars();
  const gcalMultiEnabled = isGcalMultiCalendarEnabled();
  const visibleSyncCalendars = useMemo(
    () =>
      gcalMultiEnabled
        ? listEnabledSyncCalendars(gcalConnections, allowedGoogleCalendarIds)
        : [],
    [gcalMultiEnabled, gcalConnections, allowedGoogleCalendarIds],
  );
  const orderedCalendarIds = useMemo(
    () => visibleSyncCalendars.map((c) => c.calendar_id),
    [visibleSyncCalendars],
  );
  const { filterByVisibleCalendars } = useAppointmentsCalendarFilter();
  const {
    hasConnection: hasGoogleCalendar,
    needsReconnect: gcalNeedsReconnect,
    isChecking: gcalIsChecking,
    refresh: refreshGcalStatus,
  } = useGoogleCalendarStatus();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [appointments, setAppointments] = useState<AppointmentRecord[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [view, setView] = useState<ViewType>("week");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const dayScrollRef = useRef<HTMLDivElement>(null);
  const weekScrollRef = useRef<HTMLDivElement>(null);

  // Mobile: mini-calendário expandido
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  const [mobileDatePickerOpen, setMobileDatePickerOpen] = useState(false);
  const [mobileViewPickerOpen, setMobileViewPickerOpen] = useState(false);

  const { openDraft } = useAppointmentDraft();
  const [selectedSlot, setSelectedSlot] = useState<{ date: Date; hour: number } | null>(null);
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // Current time indicator
  const [currentTime, setCurrentTime] = useState(new Date());

  // Drag and drop states
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [stageColorMap, setStageColorMap] = useState<Record<string, string>>({});

  // Derived: conflict count for sync dropdown badge
  const conflictCount = appointments.filter((a) => a.sync_status === "conflict").length;

  const displayAppointments = useMemo(
    () => filterByVisibleCalendars(appointments),
    [appointments, filterByVisibleCalendars],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevents accidental drags
      },
    })
  );

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Calculate current time position (pixels from top)
  const getCurrentTimePosition = () => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    return (hours * 60) + minutes; // 60px per hour
  };

  // Time indicator line component
  const TimeIndicatorLine = () => {
    const top = getCurrentTimePosition();
    return (
      <div 
        className="absolute left-0 right-0 z-30 pointer-events-none flex items-center"
        style={{ top: `${top}px` }}
      >
        <div className="w-3 h-3 rounded-full bg-red-500 -ml-1.5 flex-shrink-0" />
        <div className="h-0.5 bg-red-500 flex-1" />
      </div>
    );
  };

  useEffect(() => {
    if (workspaceId) {
      fetchLeads();
    }
  }, [workspaceId, currentDate, view, allowedGoogleCalendarIds, canSeeAllAppointments, memberId, isAdmin, appointmentsCalendarVisibility]);

  // Realtime: refresh appointments when DB changes (manual create, sync_to_google,
  // sync_from_google, drag/drop, cancel). Avoids "evento não aparece até atualizar".
  useEffect(() => {
    if (!workspaceId) return;
    const channelName = `appointments-rt-${workspaceId}`;
    const ch = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "appointments",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => {
        fetchLeads();
      },
    );
    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Mobile: view inicial Agenda (estilo Google Agenda)
  useEffect(() => {
    if (isMobile && view === "week") {
      setView("schedule");
    }
  }, [isMobile]);

  // Scroll inicial para 6h (horário de trabalho comum) — apenas desktop day/week
  useEffect(() => {
    if (isMobile) return;
    const scrollTo6h = () => {
      const scrollPosition = 6 * 60;
      if (view === "day" && dayScrollRef.current) {
        dayScrollRef.current.scrollTop = scrollPosition;
      } else if (view === "week" && weekScrollRef.current) {
        weekScrollRef.current.scrollTop = scrollPosition;
      }
    };
    const timer = setTimeout(scrollTo6h, 100);
    return () => clearTimeout(timer);
  }, [view, currentDate, isMobile]);

  const getDateRange = () => {
    switch (view) {
      case "day":
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
        };
      case "week":
        return {
          start: startOfWeek(currentDate, { locale: ptBR }),
          end: endOfWeek(currentDate, { locale: ptBR }),
        };
      case "month":
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate),
        };
      case "year":
        return {
          start: startOfYear(currentDate),
          end: endOfYear(currentDate),
        };
      case "schedule":
        return {
          start: startOfDay(currentDate),
          end: endOfDay(addDays(currentDate, 30)),
        };
      default:
        return {
          start: startOfWeek(currentDate, { locale: ptBR }),
          end: endOfWeek(currentDate, { locale: ptBR }),
        };
    }
  };

  const fetchLeads = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const { start, end } = getDateRange();

      const [appointmentsResult, stagesResult] = await Promise.all([
        supabase
          .from("appointments")
          .select("*")
          .eq("workspace_id", workspaceId)
          .in("status", ["scheduled", "done"])
          .not("sync_status", "eq", "suppressed")
          .gte("scheduled_date", start.toISOString())
          .lte("scheduled_date", end.toISOString())
          .order("scheduled_date", { ascending: true }),
        supabase
          .from("lead_stages")
          .select("slug, color")
          .eq("workspace_id", workspaceId),
      ]);

      if (appointmentsResult.error) throw appointmentsResult.error;
      let appts = (appointmentsResult.data || []) as AppointmentRecord[];

      if (isGcalMultiCalendarEnabled() && allowedGoogleCalendarIds !== null) {
        const allowed = new Set(allowedGoogleCalendarIds);
        appts = appts.filter(
          (a) => !a.google_calendar_id || allowed.has(a.google_calendar_id),
        );
      }

      const leadIds = [...new Set(appts.map(a => a.lead_id).filter((id): id is string => id !== null))];
      let leadsData: Lead[] = [];
      if (leadIds.length > 0) {
        const { data } = await supabase
          .from("leads")
          .select("id, name, email, phone, current_appointment_id, current_appointment_date, current_appointment_duration, stage, notes, metadata, assigned_member_id")
          .eq("workspace_id", workspaceId)
          .in("id", leadIds);
        leadsData = (data || []) as Lead[];
      }

      if (!canSeeAllAppointments) {
        const assignedByLeadId = new Map(
          leadsData.map((l) => [l.id, l.assigned_member_id ?? null]),
        );
        appts = appts.filter((a) =>
          isAppointmentVisibleToMember(a, {
            isAdmin,
            currentMemberId: memberId,
            appointmentsCalendarVisibility,
            leadAssignedMemberId: a.lead_id
              ? assignedByLeadId.get(a.lead_id)
              : null,
          }),
        );
        const visibleLeadIds = new Set(
          appts.map((a) => a.lead_id).filter((id): id is string => id !== null),
        );
        leadsData = leadsData.filter((l) => visibleLeadIds.has(l.id));
      }

      setAppointments(appts);
      setLeads(leadsData);

      if (stagesResult.data) {
        const map: Record<string, string> = {};
        for (const s of stagesResult.data) {
          map[s.slug] = s.color;
        }
        setStageColorMap(map);
      }
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
      toast({
        title: "Erro ao carregar agendamentos",
        description: "Não foi possível carregar os agendamentos.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const { syncing: autoSyncing } = useGoogleCalendarAutoSync({
    workspaceId,
    hasGoogleCalendar,
    needsReconnect: gcalNeedsReconnect,
    onComplete: fetchLeads,
  });

  const navigateDate = (direction: "prev" | "next") => {
    switch (view) {
      case "day":
        setCurrentDate(direction === "prev" ? addDays(currentDate, -1) : addDays(currentDate, 1));
        break;
      case "week":
        setCurrentDate(direction === "prev" ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
        break;
      case "month":
        setCurrentDate(direction === "prev" ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
        break;
      case "year":
        setCurrentDate(direction === "prev" ? subYears(currentDate, 1) : addYears(currentDate, 1));
        break;
      case "schedule":
        setCurrentDate(direction === "prev" ? addDays(currentDate, -7) : addDays(currentDate, 7));
        break;
    }
  };

  const getViewLabel = () => {
    const { start, end } = getDateRange();
    switch (view) {
      case "day":
        return format(currentDate, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
      case "week":
        return `${format(start, "d 'de' MMMM", { locale: ptBR })} - ${format(end, "d 'de' MMMM", { locale: ptBR })}`;
      case "month":
        return format(currentDate, "MMMM 'de' yyyy", { locale: ptBR });
      case "year":
        return format(currentDate, "yyyy", { locale: ptBR });
      case "schedule":
        return `${format(start, "d 'de' MMMM", { locale: ptBR })} - ${format(end, "d 'de' MMMM", { locale: ptBR })}`;
      default:
        return format(currentDate, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
    }
  };

  const getViewName = () => {
    switch (view) {
      case "day":
        return "Dia";
      case "week":
        return "Semana";
      case "month":
        return "Mês";
      case "year":
        return "Ano";
      case "schedule":
        return "Agenda";
      default:
        return "Semana";
    }
  };

  const weekDays =
    view === "week"
      ? eachDayOfInterval({
          start: startOfWeek(currentDate, { locale: ptBR }),
          end: endOfWeek(currentDate, { locale: ptBR }),
        })
      : [];

  const today = new Date();
  const isToday = (date: Date) => isSameDay(date, today);

  const tailwindToHex: Record<string, string> = {
    "bg-blue-500": "#3b82f6",
    "bg-green-500": "#22c55e",
    "bg-purple-500": "#a855f7",
    "bg-orange-500": "#f97316",
    "bg-red-500": "#ef4444",
    "bg-yellow-500": "#eab308",
    "bg-pink-500": "#ec4899",
    "bg-indigo-500": "#6366f1",
    "bg-teal-500": "#14b8a6",
    "bg-cyan-500": "#06b6d4",
    "bg-gray-500": "#6b7280",
    "bg-emerald-500": "#10b981",
    "bg-amber-500": "#f59e0b",
    "bg-rose-500": "#f43f5e",
    "bg-violet-500": "#8b5cf6",
    "bg-lime-500": "#84cc16",
    "bg-sky-500": "#0ea5e9",
    "bg-fuchsia-500": "#d946ef",
  };

  const getEventColor = (stage: string): string => {
    const dbColor = stageColorMap[stage];
    if (dbColor) {
      // If it's already a hex color, use directly
      if (dbColor.startsWith("#")) return dbColor;
      // Convert Tailwind class to hex
      return tailwindToHex[dbColor] || "#6b7280";
    }
    const fallback: Record<string, string> = {
      novo: "#3b82f6",
      qualificado: "#22c55e",
      proposta: "#a855f7",
      negociacao: "#f97316",
      fechado: "#ef4444",
    };
    return fallback[stage] || "#6b7280";
  };

  const mapApptToCalendarEvent = (appt: AppointmentRecord): CalendarEvent => {
    const lead = appt.lead_id ? leads.find((l) => l.id === appt.lead_id) : null;
    const start = getAppointmentStart(appt);
    const end = getAppointmentEnd(appt);
    const title = lead?.name ?? appt.summary ?? appt.attendee_name ?? "Compromisso";

    return {
      id: appt.id,
      appointmentId: appt.id,
      title,
      start,
      end,
      color: lead ? getEventColor(lead.stage) : "#3b82f6",
      google_event_id: appt.google_event_id,
      google_calendar_id: appt.google_calendar_id,
      calendarAccentColor:
        gcalMultiEnabled && appt.google_calendar_id
          ? getCalendarAccentColor(appt.google_calendar_id, orderedCalendarIds)
          : null,
      duration_minutes: appt.duration_minutes,
      service_name: appt.service_name,
      reason: appt.reason,
      notes: appt.notes,
      summary: appt.summary,
      attendee_name: appt.attendee_name,
      attendee_email: appt.attendee_email,
      origin_source: appt.origin_source,
      sync_status: appt.sync_status,
      isAllDay: appt.is_all_day,
      lead: lead
        ? {
            ...lead,
            appointmentId: appt.id,
            current_appointment_date: appt.scheduled_date,
            current_appointment_id: appt.google_event_id || lead.current_appointment_id,
            current_appointment_duration: appt.duration_minutes,
          }
        : null,
    };
  };

  const getAllDayEventsForDay = (day: Date): CalendarEvent[] => {
    return displayAppointments
      .filter((appt) => appt.is_all_day && appointmentOverlapsDay(appt, day))
      .map(mapApptToCalendarEvent);
  };

  const getEventsForDay = (day: Date): CalendarEvent[] => {
    return displayAppointments
      .filter((appt) => !appt.is_all_day && appointmentOverlapsDay(appt, day))
      .map(mapApptToCalendarEvent);
  };

  type WeekAllDayLayout = {
    event: CalendarEvent;
    startCol: number;
    span: number;
    row: number;
  };

  const layoutWeekAllDayEvents = (weekDays: Date[]): WeekAllDayLayout[] => {
    const allDayAppts = displayAppointments.filter(
      (appt) => appt.is_all_day && weekDays.some((d) => appointmentOverlapsDay(appt, d)),
    );

    const sorted = [...allDayAppts].sort((a, b) => {
      const spanA = getAllDaySpanInWeek(a, weekDays)?.span ?? 1;
      const spanB = getAllDaySpanInWeek(b, weekDays)?.span ?? 1;
      if (spanB !== spanA) return spanB - spanA;
      return getAppointmentStart(a).getTime() - getAppointmentStart(b).getTime();
    });

    const rowRanges: { startCol: number; span: number }[][] = [];
    const layouts: WeekAllDayLayout[] = [];

    const rangesOverlap = (
      aStart: number,
      aSpan: number,
      bStart: number,
      bSpan: number,
    ) => aStart < bStart + bSpan && bStart < aStart + aSpan;

    for (const appt of sorted) {
      const spanInfo = getAllDaySpanInWeek(appt, weekDays);
      if (!spanInfo) continue;

      let row = 0;
      while (
        rowRanges[row]?.some((r) =>
          rangesOverlap(spanInfo.startCol, spanInfo.span, r.startCol, r.span),
        )
      ) {
        row++;
      }
      if (!rowRanges[row]) rowRanges[row] = [];
      rowRanges[row].push(spanInfo);

      layouts.push({
        event: mapApptToCalendarEvent(appt),
        startCol: spanInfo.startCol,
        span: spanInfo.span,
        row,
      });
    }

    return layouts;
  };

  const buildEventRenderPiecesForDay = (events: CalendarEvent[], day: Date) => {
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);

    const clippedSpans = events.map((event) => {
      const start = max([event.start, dayStart]);
      const end = min([event.end, dayEnd]);
      return { id: event.id, start, end };
    });

    const pieces = buildTimedEventRenderPieces(clippedSpans);
    const eventById = new Map(events.map((event) => [event.id, event]));

    return pieces
      .map((piece) => {
        const event = eventById.get(piece.eventId);
        if (!event) return null;

        const position = getTimedEventPositionStyle(piece.start, piece.end, {
          column: piece.column,
          totalColumns: piece.totalColumns,
          leftPercent: (piece.column / piece.totalColumns) * 100,
          widthPercent: ((piece.totalColumns - piece.column) / piece.totalColumns) * 100,
        });

        return {
          key: piece.key,
          event,
          position,
        };
      })
      .filter(Boolean) as Array<{
      key: string;
      event: CalendarEvent;
      position: ReturnType<typeof getTimedEventPositionStyle>;
    }>;
  };

  const handleSlotClick = (date: Date, hour: number) => {
    if (isDragging) return; // Don't open dialog while dragging
    const slotDate = new Date(date);
    slotDate.setHours(hour, 0, 0, 0);

    if (isBefore(slotDate, new Date())) {
      toast({
        title: "Não é possível agendar no passado",
        variant: "destructive",
      });
      return;
    }

    setSelectedSlot({ date, hour });
    openDraft({
      initialDate: date,
      initialHour: hour,
      onSuccess: fetchLeads,
    });
  };

  const handleEventClick = (event: CalendarEvent) => {
    if (isDragging) return;
    setSelectedLead(event.lead);
    setSelectedEvent(event);
    setDetailsSheetOpen(true);
  };

  // Build eventData for standalone events (no lead)
  const getSelectedCalendarMeta = () => {
    if (!selectedEvent?.google_calendar_id) {
      return { googleCalendarId: undefined, calendarDisplayName: undefined };
    }
    return {
      googleCalendarId: selectedEvent.google_calendar_id,
      calendarDisplayName:
        getCalendarLabelByGoogleId(visibleSyncCalendars, selectedEvent.google_calendar_id) ??
        undefined,
    };
  };

  const getEventData = () => {
    if (!selectedEvent || selectedEvent.lead) return undefined;
    return {
      appointmentId: selectedEvent.appointmentId,
      scheduled_date: selectedEvent.start.toISOString(),
      duration_minutes: selectedEvent.duration_minutes || 60,
      google_event_id: selectedEvent.google_event_id,
      google_calendar_id: selectedEvent.google_calendar_id,
      calendarDisplayName: selectedEvent.google_calendar_id
        ? getCalendarLabelByGoogleId(visibleSyncCalendars, selectedEvent.google_calendar_id)
        : undefined,
      summary: selectedEvent.summary,
      attendee_name: selectedEvent.attendee_name,
      attendee_email: selectedEvent.attendee_email,
      origin_source: selectedEvent.origin_source,
      sync_status: selectedEvent.sync_status,
      service_name: selectedEvent.service_name,
      notes: selectedEvent.notes,
    };
  };

  const handleNewAppointment = () => {
    const now = new Date();
    const baseDay = startOfDay(currentDate);
    const todayStart = startOfDay(now);

    const isBaseToday = isSameDay(baseDay, now);
    const isBaseInPast = isBefore(baseDay, todayStart);

    // Default time:
    // - today: next hour (clamped to 8..21)
    // - future day: 09:00
    let targetDay = isBaseInPast ? todayStart : baseDay;
    let hour = 9;

    if (isBaseToday || isSameDay(targetDay, now)) {
      hour = Math.min(Math.max(now.getHours() + 1, 8), 21);
      // If day is today but already too late, move to tomorrow 09:00
      if (now.getHours() >= 21) {
        targetDay = addDays(todayStart, 1);
        hour = 9;
      }
    }

    setSelectedSlot({ date: targetDay, hour });
    openDraft({
      initialDate: targetDay,
      initialHour: hour,
      onSuccess: fetchLeads,
    });
  };

  const handleReschedule = (lead: Lead, eventMeta?: {
    appointmentId?: string;
    google_event_id?: string;
    duration_minutes?: number;
    service_name?: string;
    reason?: string;
    notes?: string;
    scheduled_date?: string;
  }) => {
    const apptDate = eventMeta?.scheduled_date || lead.current_appointment_date;
    if (apptDate) {
      const date = parseISO(apptDate);
      setSelectedSlot({ date, hour: date.getHours() });
    }
    openDraft({
      initialDate: apptDate ? parseISO(apptDate) : undefined,
      initialHour: apptDate ? parseISO(apptDate).getHours() : undefined,
      initialLeadId: lead.id,
      rescheduleContext: {
        previousGoogleEventId: eventMeta?.google_event_id || lead.current_appointment_id || undefined,
        previousAppointmentDate: apptDate || undefined,
        previousAppointmentId: eventMeta?.appointmentId || lead.appointmentId || undefined,
        previousDuration: eventMeta?.duration_minutes || lead.current_appointment_duration || undefined,
        previousServiceName: eventMeta?.service_name || (lead.metadata?.last_service_name as string) || undefined,
        previousServiceId: (lead.metadata?.last_service_id as string) || undefined,
        previousReason: eventMeta?.reason || undefined,
        previousNotes: eventMeta?.notes || lead.notes || undefined,
      },
      onSuccess: fetchLeads,
    });
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const eventData = event.active.data.current?.event as CalendarEvent;
    setActiveEvent(eventData);
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setIsDragging(false);
    setActiveEvent(null);

    const { active, over } = event;
    if (!over) return;

    const eventData = active.data.current?.event as CalendarEvent;
    const slotId = over.id as string; // Format: "2025-12-24-14" or "day-14"

    // Parse the slot ID to get date and hour
    let newDate: Date;
    const parts = slotId.split("-");
    
    if (parts[0] === "day") {
      // Day view format: "day-14"
      const hour = parseInt(parts[1], 10);
      newDate = new Date(currentDate);
      newDate.setHours(hour, 0, 0, 0);
    } else {
      // Week view format: "2025-12-24-14"
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);
      const hour = parseInt(parts[3], 10);
      newDate = new Date(year, month, day, hour, 0, 0);
    }

    // Prevent moving to the past
    if (isBefore(newDate, new Date())) {
      toast({
        title: "Não é possível agendar no passado",
        variant: "destructive",
      });
      return;
    }

    // If same time, do nothing
    if (eventData.start.getTime() === newDate.getTime()) return;

    // Update appointment
    if (eventData.lead) {
      await updateAppointmentDate(
        eventData.lead.id,
        newDate,
        eventData.lead.current_appointment_id
      );
    } else {
      await updateStandaloneAppointmentDate(eventData.appointmentId, newDate, eventData.google_event_id);
    }
  };

  const updateAppointmentDate = async (
    leadId: string,
    newDate: Date,
    googleCalendarEventId?: string
  ) => {
    // Store previous state for rollback
    const previousLead = leads.find((l) => l.id === leadId);
    const previousDate = previousLead?.current_appointment_date;
    const durationMinutes = previousLead?.current_appointment_duration || 60;

    // Optimistic update on appointments
    setAppointments((prev) =>
      prev.map((a) =>
        a.lead_id === leadId && a.status === "scheduled"
          ? { ...a, scheduled_date: newDate.toISOString() }
          : a
      )
    );
    setLeads((prev) =>
      prev.map((l) =>
        l.id === leadId
          ? { ...l, current_appointment_date: newDate.toISOString() }
          : l
      )
    );

    try {
      // Update lead in Supabase
      const { error } = await supabase
        .from("leads")
        .update({ current_appointment_date: newDate.toISOString() })
        .eq("id", leadId)
        .eq("workspace_id", workspaceId);

      if (error) throw error;

      // Update appointments table too
      await supabase
        .from("appointments")
        .update({ scheduled_date: newDate.toISOString() })
        .eq("lead_id", leadId)
        .eq("workspace_id", workspaceId)
        .eq("status", "scheduled");

      // Update in Google Calendar if exists
      if (googleCalendarEventId && hasGoogleCalendar && workspaceId) {
        const endTime = addMinutes(newDate, durationMinutes);

        try {
          const r = await invokeGoogleCalendar("reschedule_event", workspaceId, {
            eventId: googleCalendarEventId,
            newStartTime: newDate.toISOString(),
            newEndTime: endTime.toISOString(),
          });
          if (!r.ok) {
            console.error("Erro ao atualizar Google Calendar:", r.kind, r.message);
            if (r.kind === "needs_reconnect") {
              toast({
                title: "Reconecte o Google Calendar",
                description: r.message,
                variant: "destructive",
              });
            }
          }
        } catch (gcError) {
          console.error("Erro ao atualizar Google Calendar:", gcError);
        }
      }

      toast({ title: "Agendamento movido com sucesso" });
    } catch (error: any) {
      // Rollback
      if (previousDate) {
        setAppointments((prev) =>
          prev.map((a) =>
            a.lead_id === leadId && a.status === "scheduled"
              ? { ...a, scheduled_date: previousDate }
              : a
          )
        );
        setLeads((prev) =>
          prev.map((l) =>
            l.id === leadId ? { ...l, current_appointment_date: previousDate } : l
          )
        );
      }
      toast({
        title: "Erro ao mover agendamento",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateStandaloneAppointmentDate = async (
    appointmentId: string,
    newDate: Date,
    googleEventId?: string
  ) => {
    const previousAppt = appointments.find((a) => a.id === appointmentId);
    const previousDate = previousAppt?.scheduled_date;
    const durationMinutes = previousAppt?.duration_minutes || 60;

    setAppointments((prev) =>
      prev.map((a) =>
        a.id === appointmentId
          ? { ...a, scheduled_date: newDate.toISOString() }
          : a
      )
    );

    try {
      const { error } = await supabase
        .from("appointments")
        .update({ scheduled_date: newDate.toISOString() })
        .eq("id", appointmentId)
        .eq("workspace_id", workspaceId);

      if (error) throw error;

      if (googleEventId && hasGoogleCalendar && workspaceId) {
        const endTime = addMinutes(newDate, durationMinutes);
        try {
          const r = await invokeGoogleCalendar("reschedule_event", workspaceId, {
            eventId: googleEventId,
            newStartTime: newDate.toISOString(),
            newEndTime: endTime.toISOString(),
          });
          if (!r.ok) {
            console.error("Erro ao atualizar Google Calendar:", r.kind, r.message);
            if (r.kind === "needs_reconnect") {
              toast({
                title: "Reconecte o Google Calendar",
                description: r.message,
                variant: "destructive",
              });
            }
          }
        } catch (gcError) {
          console.error("Erro ao atualizar Google Calendar:", gcError);
        }
      }

      toast({ title: "Agendamento movido com sucesso" });
    } catch (error: any) {
      if (previousDate) {
        setAppointments((prev) =>
          prev.map((a) =>
            a.id === appointmentId
              ? { ...a, scheduled_date: previousDate }
              : a
          )
        );
      }
      toast({
        title: "Erro ao mover agendamento",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const renderDayView = () => {
    const events = getEventsForDay(currentDate);
    const totalHeight = HOURS.length * 60; // Altura total baseada no número de horas

    const allDayEvents = getAllDayEventsForDay(currentDate);

    return (
      <div ref={dayScrollRef} className={cn("relative h-full overflow-auto", isMobile && "pb-24")}>
        {/* All-day events banner */}
        {allDayEvents.length > 0 && (
          <div className="sticky top-0 z-20 bg-muted/50 border-b border-border px-2 py-1 space-y-1">
            {allDayEvents.map((event) => {
              const dayCount = getAllDayCalendarDayCount({
                scheduled_date: event.start.toISOString(),
                duration_minutes: event.duration_minutes,
                is_all_day: true,
              });
              return (
              <div
                key={event.id}
                className="text-xs px-2 py-1 rounded text-white cursor-pointer truncate"
                style={{ backgroundColor: event.color }}
                onClick={() => handleEventClick(event)}
              >
                {event.title}
                {dayCount > 1 ? ` · ${dayCount} dias` : " — dia inteiro"}
              </div>
              );
            })}
          </div>
        )}
        <div className="relative" style={{ minHeight: `${totalHeight}px` }}>
          <div className="flex">
            {/* Coluna de Horários - Fixa */}
            <div className="w-20 flex-shrink-0 sticky left-0 bg-background z-10">
              {HOURS.map((hour) => (
                <div key={hour} className="h-[60px] border-b border-border relative">
                  <span className="absolute top-0 right-2 text-xs text-muted-foreground font-medium leading-[60px] text-right">
                    {hour}h
                  </span>
                </div>
              ))}
            </div>

            {/* Linha Divisória */}
            <div className="w-px bg-border flex-shrink-0" />

            {/* Área Principal do Dia */}
            <div className="relative flex-1">
              {HOURS.map((hour) => (
                <DroppableSlot
                  key={hour}
                  id={`day-${hour}`}
                  hour={hour}
                  onClick={() => handleSlotClick(currentDate, hour)}
                  isHighlighted={isToday(currentDate)}
                />
              ))}

              {/* Linha indicadora de horário atual - apenas no dia de hoje */}
              {isToday(currentDate) && <TimeIndicatorLine />}

              {/* Eventos Draggable */}
              {buildEventRenderPiecesForDay(events, currentDate).map((piece) => (
                <DraggableEvent
                  key={piece.key}
                  event={piece.event}
                  position={piece.position}
                  onEventClick={handleEventClick}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = eachDayOfInterval({
      start: startOfWeek(currentDate, { locale: ptBR }),
      end: endOfWeek(currentDate, { locale: ptBR }),
    });
    const totalHeight = HOURS.length * 60;
    const weekAllDayLayouts = layoutWeekAllDayEvents(weekDays);
    const multiDayLayouts = weekAllDayLayouts.filter((l) => l.span > 1);
    const singleDayLayouts = weekAllDayLayouts.filter((l) => l.span === 1);
    const multiDayRowCount =
      multiDayLayouts.length > 0
        ? Math.max(...multiDayLayouts.map((l) => l.row)) + 1
        : 0;
    const headerBottomPad = multiDayRowCount > 0 ? 0.25 + multiDayRowCount * 1.375 : 0.5;

    return (
      <div ref={weekScrollRef} className="relative h-full overflow-auto">
        <div className="sticky top-0 z-20 bg-background border-b border-border">
          <div className="flex">
            <div
              className="w-20 flex-shrink-0 bg-background"
              style={{ paddingBottom: `${headerBottomPad}rem` }}
            />
            <div className="w-px bg-border flex-shrink-0" />
            <div className="relative flex min-w-0 flex-1">
              <div className="flex w-full">
                {weekDays.map((day, i) => {
                  const singles = singleDayLayouts.filter((l) => l.startCol === i);
                  return (
                    <div
                      key={i}
                      className={cn(
                        "flex min-w-0 flex-1 flex-col items-center border-r border-border px-1 pt-1.5 last:border-r-0",
                        isToday(day) && "bg-primary/10",
                      )}
                      style={{ paddingBottom: `${headerBottomPad}rem` }}
                    >
                      <div className="text-[10px] font-medium uppercase leading-3 tracking-wide text-muted-foreground">
                        {format(day, "EEE", { locale: ptBR })}
                      </div>
                      <div className="flex h-8 w-full shrink-0 items-center justify-center">
                        <span
                          className={cn(
                            "text-lg font-medium leading-none",
                            isToday(day) &&
                              "flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground",
                          )}
                        >
                          {format(day, "d")}
                        </span>
                      </div>
                      {singles.map((layout) => (
                        <div
                          key={layout.event.id}
                          className="mt-0.5 flex h-5 w-full cursor-pointer items-center truncate rounded-full px-2 text-[11px] font-medium leading-none text-white"
                          style={{ backgroundColor: layout.event.color }}
                          onClick={() => handleEventClick(layout.event)}
                        >
                          {layout.event.title}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {multiDayLayouts.length > 0 && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[3.125rem]">
                  {multiDayLayouts.map((layout) => (
                    <div
                      key={layout.event.id}
                      className="pointer-events-auto absolute flex h-5 cursor-pointer items-center truncate rounded-full px-2.5 text-[11px] font-medium leading-none text-white"
                      style={{
                        left: `calc(${(layout.startCol / 7) * 100}% + 4px)`,
                        width: `calc(${(layout.span / 7) * 100}% - 8px)`,
                        top: `${layout.row * 1.375}rem`,
                        backgroundColor: layout.event.color,
                      }}
                      onClick={() => handleEventClick(layout.event)}
                    >
                      {layout.event.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative" style={{ minHeight: `${totalHeight}px` }}>
          <div className="flex">
            <div className="w-20 flex-shrink-0 sticky left-0 bg-background z-10">
              {HOURS.map((hour) => (
                <div key={hour} className="h-[60px] border-b border-border relative">
                  <span className="absolute top-0 right-2 text-xs text-muted-foreground font-medium leading-[60px] text-right">
                    {hour}h
                  </span>
                </div>
              ))}
            </div>

            <div className="w-px bg-border flex-shrink-0 sticky left-20 z-10" />

            {weekDays.map((day, dayIndex) => {
              const events = getEventsForDay(day);
              const renderPieces = buildEventRenderPiecesForDay(events, day);
              return (
                <div
                  key={dayIndex}
                  className={cn(
                    "flex-1 border-r border-border last:border-r-0 relative",
                    isToday(day) && "bg-primary/5"
                  )}
                >
                  {HOURS.map((hour) => (
                    <DroppableSlot
                      key={hour}
                      id={`${format(day, "yyyy-MM-dd")}-${hour}`}
                      hour={hour}
                      onClick={() => handleSlotClick(day, hour)}
                      isHighlighted={isToday(day)}
                    />
                  ))}

                  {/* Linha indicadora de horário atual - apenas no dia de hoje */}
                  {isToday(day) && <TimeIndicatorLine />}

                  {renderPieces.map((piece) => (
                    <DraggableEvent
                      key={piece.key}
                      event={piece.event}
                      position={piece.position}
                      onEventClick={handleEventClick}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const firstDayOfWeek = monthStart.getDay();
    const daysBeforeMonth = Array.from({ length: firstDayOfWeek }, (_, i) =>
      addDays(monthStart, -firstDayOfWeek + i)
    );

    return (
      <div className="p-4">
        <div className="grid grid-cols-7 gap-1">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}

          {daysBeforeMonth.map((day, i) => (
            <div key={`before-${i}`} className="h-24 p-1 border border-border rounded bg-muted/30" />
          ))}

          {monthDays.map((day, i) => {
            const timedEvents = getEventsForDay(day);
            const allDayEvents = getAllDayEventsForDay(day);
            const events = [...allDayEvents, ...timedEvents];
            return (
              <div
                key={i}
                className={cn(
                  "h-24 p-1 border border-border rounded flex flex-col cursor-pointer hover:bg-muted/50 transition-colors",
                  isToday(day) && "bg-primary/10 ring-2 ring-primary"
                )}
                onClick={() => handleSlotClick(day, 9)}
              >
                <div className={cn("text-sm font-medium mb-1", isToday(day) && "text-primary font-bold")}>
                  {format(day, "d")}
                </div>
                <div className="flex-1 overflow-y-auto space-y-0.5">
                  {events.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      className={cn("text-[10px] px-1 py-0.5 rounded text-white truncate cursor-pointer")}
                      style={{ backgroundColor: event.color }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                    >
                      {event.isAllDay ? "" : `${format(event.start, "HH:mm")} `}
                      {event.title}
                    </div>
                  ))}
                  {events.length > 3 && (
                    <div className="text-[10px] text-muted-foreground px-1">+{events.length - 3} mais</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearView = () => {
    const yearStart = startOfYear(currentDate);
    const yearEnd = endOfYear(currentDate);
    const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

    return (
      <div className="p-4 grid grid-cols-3 gap-4">
        {months.map((month, i) => {
          const monthAppts = displayAppointments.filter((appt) => {
            const start = getAppointmentStart(appt);
            const end = getAppointmentEnd(appt);
            const mStart = startOfMonth(month);
            const mEnd = endOfMonth(month);
            return start <= mEnd && end > mStart;
          });

          return (
            <Card key={i} className="p-3">
              <h3 className="font-semibold mb-2">{format(month, "MMMM", { locale: ptBR })}</h3>
              <div className="space-y-1">
                {monthAppts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum agendamento</p>
                ) : (
                  monthAppts.slice(0, 5).map((appt) => {
                    const lead = leads.find(l => l.id === appt.lead_id);
                    return (
                    <div
                        key={appt.id}
                      className="text-xs p-1 rounded bg-muted cursor-pointer hover:bg-muted/80"
                      onClick={() => {
                          if (lead) {
                        setSelectedLead(lead);
                        setDetailsSheetOpen(true);
                          }
                      }}
                    >
                        <div className="font-medium">{lead?.name ?? appt.summary ?? appt.attendee_name ?? "Compromisso"}</div>
                      <div className="text-muted-foreground">
                          {format(parseISO(appt.scheduled_date), "d 'de' MMMM, HH:mm", { locale: ptBR })}
                      </div>
                    </div>
                    );
                  })
                )}
                {monthAppts.length > 5 && (
                  <p className="text-xs text-muted-foreground">+{monthAppts.length - 5} mais agendamentos</p>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  // Vista Agenda (mobile — estilo Google Agenda): lista por dia com separadores de semana
  const renderScheduleView = () => {
    const { start: rangeStart, end: rangeEnd } = getDateRange();
    const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

    const apptsInRange = displayAppointments.filter((appt) => {
      const start = getAppointmentStart(appt);
      const end = getAppointmentEnd(appt);
      return start <= endOfDay(rangeEnd) && end > startOfDay(rangeStart);
    });

    // Agrupar por dia e ordenar
    const dayEntries: { day: Date; events: CalendarEvent[] }[] = [];
    days.forEach((day) => {
      const events = apptsInRange
        .filter((appt) => appointmentOverlapsDay(appt, day))
        .map(mapApptToCalendarEvent)
        .sort((a, b) => a.start.getTime() - b.start.getTime());
      if (events.length > 0) dayEntries.push({ day, events });
    });

    if (dayEntries.length === 0) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-8 text-center">
          <CalendarIcon className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Nenhum agendamento nos próximos 30 dias</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={handleNewAppointment}>
            <Plus className="w-4 h-4 mr-2" />
            Criar agendamento
          </Button>
        </div>
      );
    }

    let lastWeek = -1;
    return (
      <div className={cn("h-full overflow-auto", isMobile && "pb-24")}>
        {dayEntries.map(({ day, events }) => {
          const weekNum = getWeek(day, { locale: ptBR });
          const showWeekSeparator = weekNum !== lastWeek;
          if (showWeekSeparator) lastWeek = weekNum;
          const weekStart = startOfWeek(day, { locale: ptBR });
          const weekEnd = endOfWeek(day, { locale: ptBR });

          return (
            <div key={day.toISOString()}>
              {showWeekSeparator && (
                <div className="sticky top-0 z-10 bg-muted/50 px-4 py-2 text-xs text-muted-foreground border-b border-border">
                  Semana {weekNum}, {format(weekStart, "d MMM", { locale: ptBR })} - {format(weekEnd, "d MMM", { locale: ptBR })}
                </div>
              )}
              <div className="sticky top-[36px] bg-background/95 backdrop-blur px-4 py-2 border-b border-border flex items-center gap-3 z-10">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex flex-col items-center justify-center text-xs flex-shrink-0",
                    isToday(day) ? "bg-primary text-primary-foreground" : "bg-muted"
                  )}
                >
                  <span className="font-bold text-sm">{format(day, "d")}</span>
                </div>
                <div>
                  <div className={cn("text-sm font-medium", isToday(day) && "text-primary")}>
                    {isToday(day) ? "Hoje" : format(day, "EEEE", { locale: ptBR })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(day, "d 'de' MMMM", { locale: ptBR })}
                  </div>
                </div>
              </div>
              <div className="divide-y divide-border">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer active:bg-muted transition-colors"
                    onClick={() => handleEventClick(event)}
                  >
                    <div
                      className="w-1 h-12 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: event.calendarAccentColor ?? event.color,
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{event.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
                      </div>
                      {event.lead?.phone && (
                        <div className="text-xs text-muted-foreground mt-0.5">{event.lead.phone}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMobileWeekListView = () => {
    const weekStart = startOfWeek(currentDate, { locale: ptBR });
    const weekEnd = endOfWeek(currentDate, { locale: ptBR });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    const entries = days
      .map((day) => {
        const events = getEventsForDay(day).sort((a, b) => a.start.getTime() - b.start.getTime());
        return { day, events };
      })
      .filter((entry) => entry.events.length > 0);

    if (entries.length === 0) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-8 text-center">
          <CalendarIcon className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Nenhum agendamento nesta semana</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={handleNewAppointment}>
            <Plus className="w-4 h-4 mr-2" />
            Criar agendamento
          </Button>
        </div>
      );
    }

    return (
      <div className="h-full overflow-auto pb-24">
        {entries.map(({ day, events }) => (
          <div key={day.toISOString()}>
            <div className="px-4 pt-4 pb-2">
              <div className="flex items-center justify-between">
                <div className={cn("text-sm font-medium", isToday(day) && "text-primary")}>
                  {isToday(day) ? "Hoje" : format(day, "EEEE", { locale: ptBR })}
                </div>
                <div className="text-xs text-muted-foreground">{format(day, "d 'de' MMM", { locale: ptBR })}</div>
              </div>
            </div>
            <div className="divide-y divide-border">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer active:bg-muted transition-colors"
                    onClick={() => handleEventClick(event)}
                >
                  <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: event.calendarAccentColor ?? event.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{event.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
                    </div>
                    {event.lead?.phone && (
                      <div className="text-xs text-muted-foreground mt-0.5">{event.lead.phone}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileMonthView = () => {
    const events = getEventsForDay(currentDate).sort((a, b) => a.start.getTime() - b.start.getTime());

    return (
      <div className="h-full flex flex-col">
        <div className="border-b border-border bg-background">
          <Calendar
            mode="single"
            selected={currentDate}
            onSelect={(date) => date && setCurrentDate(date)}
            locale={ptBR}
            classNames={{
              // Preencher a largura no mobile (estilo Google Calendar)
              months: "flex flex-col items-center space-y-4 w-full",
              month: "space-y-4 w-full",
              table: "w-full border-collapse space-y-1",
              head_row: "flex w-full justify-between px-2",
              row: "flex w-full mt-2 justify-between px-2",
              cell: "h-10 w-10 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
              day: "relative h-10 w-10 p-0 font-medium rounded-full aria-selected:opacity-100",
              day_selected:
                "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:text-primary-foreground focus:bg-primary/90 focus:text-primary-foreground rounded-full",
              // Dia atual: azul claro. Se estiver selecionado, mantém roxo.
              day_today:
                "rounded-full ring-1 ring-sky-500/35 bg-sky-500/15 text-sky-700 hover:bg-sky-500/20 dark:bg-sky-400/20 dark:text-sky-200 dark:hover:bg-sky-400/25 dark:ring-sky-300/35 aria-selected:!bg-sky-500/20 aria-selected:!text-sky-700 dark:aria-selected:!bg-sky-400/25 dark:aria-selected:!text-sky-200",
            }}
            modifiers={{
              hasAppointment: displayAppointments
                .map((appt) => parseISO(appt.scheduled_date)),
            }}
            modifiersClassNames={{
              hasAppointment:
                "after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-primary",
            }}
            className="p-2"
          />
        </div>

        <div className="flex-1 overflow-auto">
          <div className="px-4 py-3 border-b border-border bg-background/80 backdrop-blur">
            <div className={cn("text-sm font-medium", isToday(currentDate) && "text-primary")}>
              {isToday(currentDate) ? "Hoje" : format(currentDate, "EEEE", { locale: ptBR })}
            </div>
            <div className="text-xs text-muted-foreground">{format(currentDate, "d 'de' MMMM", { locale: ptBR })}</div>
          </div>

          {events.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <p className="text-muted-foreground">Nenhum agendamento neste dia</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={handleNewAppointment}>
                <Plus className="w-4 h-4 mr-2" />
                Criar agendamento
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border pb-24">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer active:bg-muted transition-colors"
                    onClick={() => handleEventClick(event)}
                  >
                  <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: event.calendarAccentColor ?? event.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{event.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
                    </div>
                    {event.lead?.phone && (
                      <div className="text-xs text-muted-foreground mt-0.5">{event.lead.phone}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMobileYearListView = () => {
    const yearStart = startOfYear(currentDate);
    const yearEnd = endOfYear(currentDate);
    const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

    return (
      <div className="h-full overflow-auto p-3 space-y-3 pb-24">
        {months.map((month) => {
          const monthAppts = displayAppointments
            .filter((appt) => {
              const d = parseISO(appt.scheduled_date);
              return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth();
            })
            .sort((a, b) => parseISO(a.scheduled_date).getTime() - parseISO(b.scheduled_date).getTime());

          return (
            <Card
              key={month.toISOString()}
              className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => {
                setCurrentDate(month);
                setView("month");
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold capitalize">{format(month, "MMMM", { locale: ptBR })}</div>
                  <div className="text-xs text-muted-foreground">{format(month, "yyyy", { locale: ptBR })}</div>
                </div>
                <Badge variant="secondary" className="flex-shrink-0">
                  {monthAppts.length} agendamento{monthAppts.length === 1 ? "" : "s"}
                </Badge>
              </div>

              {monthAppts.length > 0 && (
                <div className="mt-3 space-y-2">
                  {monthAppts.slice(0, 3).map((appt) => {
                    const lead = leads.find(l => l.id === appt.lead_id);
                    const start = parseISO(appt.scheduled_date);
                    return (
                      <div
                        key={appt.id}
                        className="text-sm flex items-center justify-between gap-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (lead) {
                            setSelectedLead(lead);
                            setDetailsSheetOpen(true);
                          }
                        }}
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">{lead?.name ?? appt.summary ?? appt.attendee_name ?? "Compromisso"}</div>
                          <div className="text-xs text-muted-foreground">
                            {format(start, "d MMM, HH:mm", { locale: ptBR })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {monthAppts.length > 3 && (
                    <div className="text-xs text-muted-foreground">+{monthAppts.length - 3} mais</div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  const renderMobileView = () => {
    switch (view) {
      case "schedule":
        return renderScheduleView();
      case "month":
        return renderMobileMonthView();
      case "week":
        return renderMobileWeekListView();
      case "year":
        return renderMobileYearListView();
      case "day":
        return renderDayView();
      default:
        return renderScheduleView();
    }
  };

  const renderView = () => {
    switch (view) {
      case "day":
        return renderDayView();
      case "week":
        return renderWeekView();
      case "month":
        return renderMonthView();
      case "year":
        return renderYearView();
      case "schedule":
        return renderScheduleView();
      default:
        return renderWeekView();
    }
  };

  if (!isMobile) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden bg-background">
        {/* Header */}
        <div className="border-b border-border px-6 py-3 flex items-center justify-between bg-background">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                {format(today, "d")}
              </div>
              <h1 className="text-xl font-medium">Agenda</h1>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
                Hoje
              </Button>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => navigateDate("prev")}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => navigateDate("next")}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Banner/Dropdown para seleção de data */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 min-w-[200px] justify-start">
                    <CalendarIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">{getViewLabel()}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={currentDate}
                    onSelect={(date) => date && setCurrentDate(date)}
                    locale={ptBR}
                    modifiers={{
                      hasAppointment: displayAppointments
                        .map((appt) => parseISO(appt.scheduled_date)),
                    }}
                    modifiersStyles={{
                      hasAppointment: {
                        fontWeight: "bold",
                        backgroundColor: "hsl(var(--primary) / 0.1)",
                        borderRadius: "0.5rem",
                      },
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <GoogleCalendarReconnectBadge />
            {workspaceId && (
              <CalendarSyncDropdown
                workspaceId={workspaceId}
                hasGoogleCalendar={hasGoogleCalendar}
                conflictCount={conflictCount}
                autoSyncing={autoSyncing}
                onSyncComplete={fetchLeads}
              />
            )}
            <Button onClick={handleNewAppointment} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Novo Agendamento
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  {getViewName()}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setView("day")}>Dia</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setView("week")}>Semana</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setView("month")}>Mês</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setView("year")}>Ano</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="px-6 pt-3 shrink-0">
          <GoogleCalendarAgendaReconnectAlert
            needsReconnect={gcalNeedsReconnect}
            hasConnection={hasGoogleCalendar}
            isChecking={gcalIsChecking}
            refresh={refreshGcalStatus}
          />
        </div>

        {/* Visualização do Calendário com DndContext */}
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex-1 min-h-0">{renderView()}</div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeEvent && (
              <div
                className="rounded px-3 py-2 text-xs text-white shadow-lg opacity-90 min-w-[120px]"
                style={{ backgroundColor: activeEvent.color }}
              >
                <div className="font-medium">{activeEvent.title}</div>
                <div className="text-white/80">{format(activeEvent.start, "HH:mm")}</div>
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Appointment Details Sheet */}
        {workspaceId && (
        <AppointmentDetailsSheet
            open={detailsSheetOpen}
            onOpenChange={setDetailsSheetOpen}
            lead={selectedLead}
            workspaceId={workspaceId}
            onUpdate={fetchLeads}
            onReschedule={handleReschedule}
            hasGoogleCalendar={hasGoogleCalendar}
            googleCalendarId={getSelectedCalendarMeta().googleCalendarId}
            calendarDisplayName={getSelectedCalendarMeta().calendarDisplayName}
            eventData={getEventData()}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-background">
      {/* Mobile Header */}
      <div className="border-b border-border px-3 py-2 flex items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <Drawer open={mobileDatePickerOpen} onOpenChange={setMobileDatePickerOpen}>
            <DrawerTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 px-2 font-medium capitalize min-w-0 max-w-[48vw] justify-start"
              >
                <span className="truncate">{format(currentDate, "MMMM", { locale: ptBR })}</span>
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="text-left">
                <DrawerTitle>Selecionar data</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-6">
                <Calendar
                  mode="single"
                  selected={currentDate}
                  onSelect={(date) => {
                    if (!date) return;
                    setCurrentDate(date);
                    setMobileDatePickerOpen(false);
                  }}
                  locale={ptBR}
                  classNames={{
                    head_row: "flex w-full justify-between px-2",
                    row: "flex w-full mt-2 justify-between px-2",
                    cell: "h-10 w-10 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                    day: "relative h-10 w-10 p-0 font-medium rounded-full aria-selected:opacity-100",
                    day_selected:
                      "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:text-primary-foreground focus:bg-primary/90 focus:text-primary-foreground rounded-full",
                    day_today:
                      "rounded-full ring-1 ring-sky-500/35 bg-sky-500/15 text-sky-700 hover:bg-sky-500/20 dark:bg-sky-400/20 dark:text-sky-200 dark:hover:bg-sky-400/25 dark:ring-sky-300/35 aria-selected:!bg-sky-500/20 aria-selected:!text-sky-700 dark:aria-selected:!bg-sky-400/25 dark:aria-selected:!text-sky-200",
                  }}
                  modifiers={{
                    hasAppointment: displayAppointments
                      .map((appt) => parseISO(appt.scheduled_date)),
                  }}
                  modifiersClassNames={{
                    hasAppointment:
                      "after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-primary",
                  }}
                />
              </div>
            </DrawerContent>
          </Drawer>

          <span className="text-sm text-muted-foreground flex-shrink-0 tabular-nums">
            {format(currentDate, "yyyy", { locale: ptBR })}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {workspaceId && (
            <CalendarSyncDropdown
              workspaceId={workspaceId}
              hasGoogleCalendar={hasGoogleCalendar}
              conflictCount={conflictCount}
              autoSyncing={autoSyncing}
              onSyncComplete={fetchLeads}
            />
          )}
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigateDate("prev")}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setCurrentDate(new Date())}>
            Hoje
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigateDate("next")}>
            <ChevronRight className="h-5 w-5" />
          </Button>

          <Drawer open={mobileViewPickerOpen} onOpenChange={setMobileViewPickerOpen}>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 px-2">
                {getViewName()}
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="text-left">
                <DrawerTitle>Visualização</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-6 grid gap-2">
                <Button
                  variant={view === "schedule" ? "default" : "outline"}
                  className="justify-start"
                  onClick={() => {
                    setView("schedule");
                    setMobileViewPickerOpen(false);
                  }}
                >
                  Agenda
                </Button>
                <Button
                  variant={view === "month" ? "default" : "outline"}
                  className="justify-start"
                  onClick={() => {
                    setView("month");
                    setMobileViewPickerOpen(false);
                  }}
                >
                  Mês
                </Button>
                <Button
                  variant={view === "day" ? "default" : "outline"}
                  className="justify-start"
                  onClick={() => {
                    setView("day");
                    setMobileViewPickerOpen(false);
                  }}
                >
                  Dia
                </Button>
                <Button
                  variant={view === "week" ? "default" : "outline"}
                  className="justify-start"
                  onClick={() => {
                    setView("week");
                    setMobileViewPickerOpen(false);
                  }}
                >
                  Semana
                </Button>
                <Button
                  variant={view === "year" ? "default" : "outline"}
                  className="justify-start"
                  onClick={() => {
                    setView("year");
                    setMobileViewPickerOpen(false);
                  }}
                >
                  Ano
                </Button>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      <div className="px-3 pt-2 shrink-0">
        <GoogleCalendarAgendaReconnectAlert
          needsReconnect={gcalNeedsReconnect}
          hasConnection={hasGoogleCalendar}
          isChecking={gcalIsChecking}
          refresh={refreshGcalStatus}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {view === "day" ? (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="h-full min-h-0">{renderMobileView()}</div>

            <DragOverlay>
              {activeEvent && (
                <div
                  className="rounded px-3 py-2 text-xs text-white shadow-lg opacity-90 min-w-[120px]"
                  style={{ backgroundColor: activeEvent.color }}
                >
                  <div className="font-medium">{activeEvent.title}</div>
                  <div className="text-white/80">{format(activeEvent.start, "HH:mm")}</div>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="h-full">{renderMobileView()}</div>
        )}
      </div>

      {/* FAB (Mobile) */}
      <Button
        onClick={handleNewAppointment}
        size="icon"
        className={cn(
          "fixed right-4 z-50 h-14 w-14 rounded-full shadow-lg",
          "bottom-[calc(env(safe-area-inset-bottom)+1rem)]"
        )}
        aria-label="Novo agendamento"
      >
        <Plus className="h-6 w-6" />
      </Button>

      {/* Appointment Details Sheet */}
      {workspaceId && (
        <AppointmentDetailsSheet
          open={detailsSheetOpen}
          onOpenChange={setDetailsSheetOpen}
          lead={selectedLead}
          workspaceId={workspaceId}
          onUpdate={fetchLeads}
          onReschedule={handleReschedule}
          hasGoogleCalendar={hasGoogleCalendar}
          googleCalendarId={getSelectedCalendarMeta().googleCalendarId}
          calendarDisplayName={getSelectedCalendarMeta().calendarDisplayName}
          eventData={getEventData()}
        />
      )}
    </div>
  );
}
